/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the application.
 * Wraps Okta authentication with additional AWS credential management.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { oktaAuth, authState } = useOktaAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [awsCredentials, setAwsCredentials] = useState(null);

  useEffect(() => {
    if (!authState) {
      setLoading(true);
      return;
    }

    if (authState.isAuthenticated) {
      // Fetch user information from Okta
      oktaAuth.getUser()
        .then((info) => {
          setUserInfo(info);
          console.log('User authenticated:', info.email);
        })
        .catch((err) => {
          console.error('Error fetching user info:', err);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setUserInfo(null);
      setAwsCredentials(null);
      setLoading(false);
    }
  }, [authState, oktaAuth]);

  /**
   * Get the current access token
   * @returns {Promise<string|null>} Access token or null
   */
  const getAccessToken = async () => {
    try {
      const token = await oktaAuth.getAccessToken();
      return token;
    } catch (err) {
      console.error('Error getting access token:', err);
      return null;
    }
  };

  /**
   * Get the current ID token
   * @returns {Promise<string|null>} ID token or null
   */
  const getIdToken = async () => {
    try {
      const token = await oktaAuth.getIdToken();
      return token;
    } catch (err) {
      console.error('Error getting ID token:', err);
      return null;
    }
  };

  /**
   * Login to Okta
   */
  const login = async () => {
    await oktaAuth.signInWithRedirect();
  };

  /**
   * Logout from Okta and clear local state
   */
  const logout = async () => {
    await oktaAuth.signOut();
    setUserInfo(null);
    setAwsCredentials(null);
  };

  /**
   * Get AWS credentials using Okta token
   * This exchanges the Okta token for AWS credentials via Cognito Identity Pool
   */
  const getAwsCredentials = async () => {
    if (awsCredentials && awsCredentials.expiration > Date.now()) {
      return awsCredentials;
    }

    try {
      const idToken = await getIdToken();
      if (!idToken) {
        throw new Error('No ID token available');
      }

      // TODO: Implement AWS credential exchange
      // This would typically involve:
      // 1. Calling Cognito Identity Pool with Okta token
      // 2. Or calling your backend to exchange token for AWS credentials
      // 3. Or using AWS STS AssumeRoleWithWebIdentity
      
      console.log('AWS credential exchange not yet implemented');
      console.log('For now, API Gateway will validate Okta token directly');
      
      return null;
    } catch (err) {
      console.error('Error getting AWS credentials:', err);
      return null;
    }
  };

  const value = {
    // Okta auth state
    isAuthenticated: authState?.isAuthenticated || false,
    isLoading: loading || !authState,
    
    // User information
    user: userInfo,
    
    // Token methods
    getAccessToken,
    getIdToken,
    
    // Auth actions
    login,
    logout,
    
    // AWS integration
    getAwsCredentials,
    awsCredentials
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use authentication context
 * @returns {object} Authentication context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
